import React, { useState, useRef, useEffect } from 'react';

export default function AnalogTimePicker({ selectedTime, onChange, placeholderText }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState(12);
  const [minutes, setMinutes] = useState(0);
  const [isAM, setIsAM] = useState(true);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (selectedTime) {
      const date = new Date(selectedTime);
      const hour24 = date.getHours();
      const hour12 = hour24 % 12 || 12;
      setHours(hour12);
      setMinutes(date.getMinutes());
      setIsAM(hour24 < 12);
    }
  }, [selectedTime]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatTime = (h, m, am) => {
    const hour12 = h % 12 || 12;
    const ampm = am ? 'AM' : 'PM';
    const minStr = m.toString().padStart(2, '0');
    return `${hour12}:${minStr} ${ampm}`;
  };

  const handleConfirm = () => {
    const date = new Date();
    let hour24 = hours;
    if (!isAM && hours !== 12) {
      hour24 = hours + 12;
    } else if (isAM && hours === 12) {
      hour24 = 0;
    }
    date.setHours(hour24);
    date.setMinutes(minutes);
    date.setSeconds(0);
    onChange(date);
    setIsOpen(false);
  };

  const incrementHour = () => {
    setHours((prev) => {
      if (prev >= 12) return 1;
      return prev + 1;
    });
  };

  const decrementHour = () => {
    setHours((prev) => {
      if (prev <= 1) return 12;
      return prev - 1;
    });
  };

  const incrementMinute = () => {
    setMinutes((prev) => {
      if (prev >= 59) return 0;
      return prev + 1;
    });
  };

  const decrementMinute = () => {
    setMinutes((prev) => {
      if (prev <= 0) return 59;
      return prev - 1;
    });
  };

  const displayTime = selectedTime ? formatTime(hours, minutes, isAM) : null;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div
        className="cs_form_field cs_timepicker"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: 'pointer',
          paddingRight: '70px',
          width: '100%',
          color: displayTime ? 'inherit' : '#999',
          minHeight: '50px',
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
      >
        <span style={{ 
          display: 'block', 
          width: '100%', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          paddingRight: '0'
        }}>
          {displayTime || placeholderText}
        </span>
      </div>
      {isOpen && (
        <div
          ref={pickerRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1000,
            backgroundColor: '#fff',
            borderRadius: '15px',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
            padding: '25px',
            marginTop: '10px',
            minWidth: '280px'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#6550C9', marginBottom: '15px', fontFamily: 'monospace' }}>
              {formatTime(hours, minutes, isAM)}
            </div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', marginBottom: '20px' }}>
            {/* Hour selector */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Hour</div>
              <button
                onClick={incrementHour}
                style={{
                  width: '40px',
                  height: '35px',
                  border: '1px solid #ddd',
                  borderRadius: '8px 8px 0 0',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#6550C9'
                }}
              >
                ↑
              </button>
              <div style={{
                width: '60px',
                height: '50px',
                border: '1px solid #6550C9',
                borderRadius: '0',
                backgroundColor: 'rgba(101, 80, 201, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: '700',
                color: '#6550C9'
              }}>
                {hours.toString().padStart(2, '0')}
              </div>
              <button
                onClick={decrementHour}
                style={{
                  width: '40px',
                  height: '35px',
                  border: '1px solid #ddd',
                  borderRadius: '0 0 8px 8px',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#6550C9'
                }}
              >
                ↓
              </button>
            </div>

            <div style={{ fontSize: '32px', fontWeight: '700', color: '#6550C9', alignSelf: 'center', marginTop: '25px' }}>:</div>

            {/* Minute selector */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Minute</div>
              <button
                onClick={incrementMinute}
                style={{
                  width: '40px',
                  height: '35px',
                  border: '1px solid #ddd',
                  borderRadius: '8px 8px 0 0',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#6550C9'
                }}
              >
                ↑
              </button>
              <div style={{
                width: '60px',
                height: '50px',
                border: '1px solid #6550C9',
                borderRadius: '0',
                backgroundColor: 'rgba(101, 80, 201, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: '700',
                color: '#6550C9'
              }}>
                {minutes.toString().padStart(2, '0')}
              </div>
              <button
                onClick={decrementMinute}
                style={{
                  width: '40px',
                  height: '35px',
                  border: '1px solid #ddd',
                  borderRadius: '0 0 8px 8px',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#6550C9'
                }}
              >
                ↓
              </button>
            </div>
          </div>

          {/* AM/PM selector */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '20px' }}>
            <button
              onClick={() => setIsAM(true)}
              style={{
                padding: '10px 30px',
                borderRadius: '8px',
                border: isAM ? '2px solid #6550C9' : '1px solid #ddd',
                backgroundColor: isAM ? '#e6f2ff' : '#fff',
                color: isAM ? '#6550C9' : '#555',
                cursor: 'pointer',
                fontWeight: isAM ? '700' : '400',
                fontSize: '16px'
              }}
            >
              AM
            </button>
            <button
              onClick={() => setIsAM(false)}
              style={{
                padding: '10px 30px',
                borderRadius: '8px',
                border: !isAM ? '2px solid #6550C9' : '1px solid #ddd',
                backgroundColor: !isAM ? '#e6f2ff' : '#fff',
                color: !isAM ? '#6550C9' : '#555',
                cursor: 'pointer',
                fontWeight: !isAM ? '700' : '400',
                fontSize: '16px'
              }}
            >
              PM
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                backgroundColor: '#fff',
                color: '#555',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#6550C9',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

